import { ReactNode } from 'react';
import styled from 'styled-components';
import { popoverUnderline } from './popoverUnderline';
import { WithUncontrolledPopover } from './WithUncontrolledPopover';
import { PlainTextPopover } from './PlainTextPopover';

// We want to exclude inline JSX, as that is not rendered lazily on-demand
export type DefinitionContent =
  | (() => ReactNode)
  | string
  | number
  | false
  | null
  | undefined;

export type DefinitionPopoverProps = {
  definition: DefinitionContent;
  children: ReactNode;
};

const UnderlinedText = styled.span`
  ${popoverUnderline}
`;

export const renderDefinitionContent = (
  content: DefinitionContent
): ReactNode => {
  if (typeof content === 'string' || typeof content === 'number') {
    return <PlainTextPopover>{content}</PlainTextPopover>;
  }
  if (typeof content === 'function') {
    return content();
  }
  return null;
};

/**
 * Inline text with a dotted underline that reveals a definition or explanation
 * in a popover on hover.
 */
export const DefinitionPopover = ({
  definition,
  children,
}: DefinitionPopoverProps) => {
  if (!definition) {
    return <>{children}</>;
  }

  const PopoverContent = () => {
    const rendered = renderDefinitionContent(definition);
    return rendered ? <>{rendered}</> : null;
  };

  return (
    <WithUncontrolledPopover
      content={PopoverContent}
      placement={{ side: 'bottom', overflow: 'center' }}
    >
      <UnderlinedText>{children}</UnderlinedText>
    </WithUncontrolledPopover>
  );
};
